/* Collective Finds — cinematic product page behaviour (sections cf-cine-*).
   One scroll loop drives the word reveal and picture-size steps; clips play
   only on screen; the sticky bar adds to basket through the page's product form. */
(() => {
  if (window.cfCineLoaded) return;
  window.cfCineLoaded = true;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const updaters = [];

  // ---------- Statement: light up word by word ----------
  function initWords(el) {
    const words = el.textContent.trim().split(/\s+/);
    el.textContent = '';
    const spans = words.map((w, i) => {
      const s = document.createElement('span');
      s.className = 'cf-w';
      s.textContent = w;
      el.appendChild(s);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      return s;
    });
    el.classList.add('is-split');
    if (reduce) { spans.forEach((s) => s.classList.add('is-on')); return; }
    updaters.push(() => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = clamp((vh * 0.82 - r.top) / (vh * 0.55), 0, 1);
      const n = Math.round(p * spans.length);
      spans.forEach((s, i) => s.classList.toggle('is-on', i < n));
    });
  }

  // ---------- Picture size: step the frame 40" -> 80" -> 130" ----------
  function initSize(section) {
    const frame = section.querySelector('.cf-cine-size__frame');
    const num = section.querySelector('.cf-cine-size__num');
    const steps = [...section.querySelectorAll('.cf-cine-size__steps li')];
    if (!frame || !steps.length) return;
    const max = parseFloat(steps[steps.length - 1].dataset.inches) || 1;
    function set(k) {
      const inches = parseFloat(steps[k].dataset.inches) || max;
      frame.style.setProperty('--s', (inches / max).toFixed(3));
      if (num) num.textContent = steps[k].dataset.label || inches + '″';
      steps.forEach((s, j) => s.classList.toggle('is-on', j === k));
    }
    if (reduce) { set(steps.length - 1); return; }
    // Step through the sizes on a timer while the section is on screen, so scrolling never stops here.
    let k = 0;
    let timer = null;
    set(0);
    new IntersectionObserver((es) => {
      if (es[0].isIntersecting) {
        if (!timer) timer = setInterval(() => { k = (k + 1) % steps.length; set(k); }, 2200);
      } else if (timer) {
        clearInterval(timer);
        timer = null;
        k = 0;
        set(0);
      }
    }, { threshold: 0.35 }).observe(section);
  }

  // ---------- Clips: loops play only while on screen; play-once clips run once and stop ----------
  function initClips() {
    // Clips ship with preload="none" so they cost nothing on arrival. Once the visitor starts
    // scrolling, fetch each one when it's within about a screen, so it's ready when it arrives.
    const lazy = document.querySelectorAll('.cf-cine video[preload="none"]');
    const lio = new IntersectionObserver((es) => {
      es.forEach((e) => {
        if (!e.isIntersecting) return;
        const v = e.target;
        lio.unobserve(v);
        v.preload = 'auto';
        if (v.readyState === 0 && v.paused) v.load();
      });
    }, { rootMargin: '100% 0px' });
    const startLazy = () => {
      window.removeEventListener('scroll', startLazy);
      lazy.forEach((v) => lio.observe(v));
    };
    if (window.scrollY > 0) startLazy(); else window.addEventListener('scroll', startLazy, { passive: true });

    const loops = document.querySelectorAll('.cf-cine video[data-cf-autoplay]');
    const io = new IntersectionObserver((es) => {
      es.forEach((e) => {
        const v = e.target;
        if (e.isIntersecting && !reduce) { v.play().catch(() => {}); } else { v.pause(); }
      });
    }, { threshold: 0.25 });
    loops.forEach((v) => io.observe(v));

    const once = document.querySelectorAll('.cf-cine video[data-cf-playonce]');
    const oio = new IntersectionObserver((es) => {
      es.forEach((e) => {
        if (!e.isIntersecting) return;
        const v = e.target;
        oio.unobserve(v);
        if (reduce) {
          // Show the end of the move as a still.
          const end = () => { v.currentTime = Math.max(0, v.duration - 0.05); };
          if (v.readyState >= 1) end(); else v.addEventListener('loadedmetadata', end, { once: true });
          return;
        }
        v.play().catch(() => {});
      });
    }, { threshold: 0.55 });
    once.forEach((v) => oio.observe(v));

    if (reduce) document.querySelectorAll('.cf-cine-hero video').forEach((v) => v.pause());
  }

  // ---------- Buy links and sticky bar ----------
  function buyForm() {
    return document.querySelector('.cf-cine-buy product-form') || document.querySelector('[id^="MainProduct-"] product-form');
  }
  function initBuyLinks() {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href="#buy"]');
      if (!a) return;
      const t = document.getElementById('buy') || document.querySelector('[id^="MainProduct-"]');
      if (!t) return;
      e.preventDefault();
      t.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    });
  }
  function initBar(bar) {
    const btn = bar.querySelector('.cf-cine-bar__btn');
    const hero = document.querySelector('.cf-cine-hero');
    const form = buyForm();
    let heroOut = !hero;
    let formIn = false;
    function update() {
      const show = heroOut && !formIn;
      bar.classList.toggle('is-visible', show);
      document.body.classList.toggle('cf-bar-on', show);
    }
    if (hero) {
      new IntersectionObserver((es) => { heroOut = !es[0].isIntersecting; update(); }, { threshold: 0 }).observe(hero);
    }
    if (form) {
      new IntersectionObserver((es) => { formIn = es[0].isIntersecting; update(); }, { threshold: 0 }).observe(form);
    }
    if (!btn) return;
    btn.addEventListener('click', () => {
      const f = buyForm();
      const submit = f && f.querySelector('.product-form__submit');
      if (submit && !submit.disabled) {
        btn.classList.add('is-busy');
        submit.click();
        setTimeout(() => btn.classList.remove('is-busy'), 1500);
        return;
      }
      // No product form on the page (shouldn't happen): add with a plain cart post.
      const id = btn.dataset.variantId;
      if (!id) return;
      const el = document.createElement('form');
      el.method = 'post';
      el.action = (window.routes && window.routes.cart_add_url) || '/cart/add';
      el.innerHTML = '<input type="hidden" name="id" value="' + id + '"><input type="hidden" name="quantity" value="1">';
      document.body.appendChild(el);
      el.submit();
    });
  }

  // ---------- Header height, for the hero to fill the first screen ----------
  function headerHeight() {
    const h = document.querySelector('.section-header');
    if (h) document.documentElement.style.setProperty('--cf-header-h', h.offsetHeight + 'px');
  }

  let ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => { ticking = false; updaters.forEach((f) => f()); });
  }

  function init() {
    headerHeight();
    document.querySelectorAll('[data-cf-words]').forEach(initWords);
    document.querySelectorAll('[data-cf-size]').forEach(initSize);
    initClips();
    initBuyLinks();
    document.querySelectorAll('[data-cf-bar]').forEach(initBar);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => { headerHeight(); onScroll(); });
    onScroll();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
